import { oc } from "@orpc/contract";
import { z } from "zod";
import { Action, Subject } from "@erp/authz";

/**
 * Authorization RPC Contracts
 *
 * Defines the contract for permission checking and management endpoints
 */

/**
 * Action enum values for Zod validation
 * Derived from the shared Action enum in @erp/authz
 */
const ActionSchema = z.enum(Action);

/**
 * Subject enum values for Zod validation
 * Derived from the shared Subject enum in @erp/authz
 */
const SubjectSchema = z.enum(Subject);

// ============================================
// Permission Check Contract
// ============================================

/**
 * Input schema for permission check
 */
export const AuthzCanInputSchema = z.object({
  action: ActionSchema,
  subject: SubjectSchema,
  record: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Output schema for permission check
 */
export const AuthzCanOutputSchema = z.object({
  allowed: z.boolean(),
});

/**
 * Permission check contract
 */
export const authzCanContract = oc
  .input(AuthzCanInputSchema)
  .output(AuthzCanOutputSchema);

// ============================================
// List Available Permissions Contract
// ============================================

/**
 * Schema for a single permission definition
 */
const PermissionDefinitionSchema = z.object({
  action: ActionSchema,
  subject: SubjectSchema,
});

/**
 * Schema for domain/subdomain grouping
 */
const DomainGroupSchema = z.object({
  domain: z.string(),
  subdomains: z.array(
    z.object({
      subdomain: z.string(),
      subjects: z.array(SubjectSchema),
    }),
  ),
});

/**
 * Schema for valid actions per subject mapping
 */
const ValidActionsPerSubjectSchema = z.record(
  SubjectSchema,
  z.array(ActionSchema),
);

/**
 * Output schema for listing available permissions
 */
export const ListAvailablePermissionsOutputSchema = z.object({
  permissions: z.array(PermissionDefinitionSchema),
  actions: z.array(ActionSchema),
  subjects: z.array(SubjectSchema),
  domainGroups: z.array(DomainGroupSchema),
  validActionsPerSubject: ValidActionsPerSubjectSchema,
});

/**
 * List available permissions contract
 * Returns all possible action/subject combinations
 */
export const listAvailablePermissionsContract = oc.output(
  ListAvailablePermissionsOutputSchema,
);

// ============================================
// Get Group Permissions Contract
// ============================================

/**
 * Input schema for getting group permissions
 */
export const GetGroupPermissionsInputSchema = z.object({
  groupId: z.uuid(),
});

/**
 * Schema for a single group permission
 */
const GroupPermissionSchema = z.object({
  id: z.uuid(),
  action: ActionSchema,
  subject: SubjectSchema,
  domain: z.string().nullable(),
  subdomain: z.string().nullable(),
  description: z.string().nullable(),
});

/**
 * Output schema for getting group permissions
 */
export const GetGroupPermissionsOutputSchema = z.object({
  groupId: z.uuid(),
  groupName: z.string(),
  permissions: z.array(GroupPermissionSchema),
});

/**
 * Get group permissions contract
 */
export const getGroupPermissionsContract = oc
  .input(GetGroupPermissionsInputSchema)
  .output(GetGroupPermissionsOutputSchema);

// ============================================
// Set Group Permissions Contract
// ============================================

/**
 * Schema for a permission to set
 */
const PermissionToSetSchema = z.object({
  action: ActionSchema,
  subject: SubjectSchema,
  domain: z.string().nullable().optional(),
  subdomain: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});

/**
 * Input schema for setting group permissions
 */
export const SetGroupPermissionsInputSchema = z.object({
  groupId: z.uuid(),
  permissions: z.array(PermissionToSetSchema),
});

/**
 * Output schema for setting group permissions
 */
export const SetGroupPermissionsOutputSchema = z.object({
  success: z.boolean(),
  groupId: z.uuid(),
  permissionCount: z.number(),
});

/**
 * Set group permissions contract
 * Replaces all permissions for a group with the provided list
 */
export const setGroupPermissionsContract = oc
  .input(SetGroupPermissionsInputSchema)
  .output(SetGroupPermissionsOutputSchema);

// ============================================
// Authorization contracts namespace
// ============================================

export const authzContracts = {
  can: authzCanContract,
  listAvailablePermissions: listAvailablePermissionsContract,
  getGroupPermissions: getGroupPermissionsContract,
  setGroupPermissions: setGroupPermissionsContract,
} as const;

// Export types
export type AuthzCanInput = z.infer<typeof AuthzCanInputSchema>;
export type AuthzCanOutput = z.infer<typeof AuthzCanOutputSchema>;

export type ListAvailablePermissionsOutput = z.infer<
  typeof ListAvailablePermissionsOutputSchema
>;
export type GetGroupPermissionsInput = z.infer<
  typeof GetGroupPermissionsInputSchema
>;
export type GetGroupPermissionsOutput = z.infer<
  typeof GetGroupPermissionsOutputSchema
>;
export type SetGroupPermissionsInput = z.infer<
  typeof SetGroupPermissionsInputSchema
>;
export type SetGroupPermissionsOutput = z.infer<
  typeof SetGroupPermissionsOutputSchema
>;
